// CheckpointManager.jsx
import React, { useState, useEffect, useContext } from 'react';
import { MapPin, Plus, Edit, Trash, X, Map, Smartphone, AlertCircle, Info, HelpCircle } from 'lucide-react';
import MapLocationPicker from './utils/MapLocationPicker';
import { AuthContext } from "../../context/AuthContext";
import NFCReader from './NFCReader';
import './styles/CheckpointManager.scss';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase'; 
import { Search } from 'lucide-react';
import NfcTagSelector from './utils/NfcTagSelector'; 

const CheckpointManager = ({ checkpoints = [], onChange }) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [showNFCReader, setShowNFCReader] = useState(false);
  const [nfcSupported, setNfcSupported] = useState(null);
  const [showNfcSelector, setShowNfcSelector] = useState(false);
  const [nfcTags, setNfcTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState('gps'); // 'gps', 'nfc', 'both'
  
  const [newCheckpoint, setNewCheckpoint] = useState({
    id: '',
    name: '',
    description: '',
    location: '',
    verificationMethod: 'gps', // Nuevo campo para el método de verificación
    nfcTag: null,  // Datos del tag NFC (opcional)
    coordinates: {
      latitude: '',
      longitude: '',
      radius: 10 // Radio en metros por defecto
    }
  });

  const { currentService } = useContext(AuthContext);

  // Cargar etiquetas NFC disponibles (siempre, independientemente del soporte NFC)
  useEffect(() => {
    if (currentService) {
      checkNfcSupport();
      // Cargamos las etiquetas NFC siempre, independientemente del soporte
      fetchNfcTags();
    }
  }, [currentService]);

  // Comprobar si NFC está disponible
  const checkNfcSupport = () => {
    const isSupported = 'NDEFReader' in window;
    setNfcSupported(isSupported);
  };

  const resetForm = () => {
    setNewCheckpoint({
      id: '',
      name: '',
      description: '',
      location: '',
      verificationMethod: 'gps',
      nfcTag: null,
      coordinates: {
        latitude: '',
        longitude: '',
        radius: 10
      }
    });
    setVerificationMethod('gps');
    setIsAddingNew(false);
    setEditingIndex(null);
    setShowNFCReader(false);
  };

  const fetchNfcTags = async () => {
    try {
      setLoadingTags(true);
      const tagsRef = collection(db, 'servicios', currentService.id, 'nfcTags');
      const querySnapshot = await getDocs(tagsRef);

      // Obtener todas las etiquetas, incluso las ya asignadas
      // (para permitir reusar etiquetas si es necesario)
      const tagsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setNfcTags(tagsList);
      setLoadingTags(false);
    } catch (err) {
      console.error('Error al cargar etiquetas NFC:', err);
      setLoadingTags(false);
    }
  };

  const handleOpenNfcSelector = () => {
    fetchNfcTags();
    setShowNfcSelector(true);
  };

  const handleSelectNfcTag = async (tag) => {
    setNewCheckpoint({
      ...newCheckpoint,
      nfcTag: {
        id: tag.tagId,
        name: tag.name,
        data: tag.nfcData || {}
      },
      verificationMethod: verificationMethod === 'gps' ? 'both' : 'nfc'
    });

    // Si estamos editando un punto existente, actualizar la referencia en la etiqueta
    if (newCheckpoint.id && tag.id) {
      try {
        await updateDoc(doc(db, 'servicios', currentService.id, 'nfcTags', tag.id), {
          checkpointId: newCheckpoint.id,
          checkpointName: newCheckpoint.name,
          updatedAt: new Date()
        });
      } catch (err) {
        console.error('Error al actualizar referencia de etiqueta:', err);
      }
    }

    setShowNfcSelector(false);
  };

  const handleVerificationMethodChange = (method) => {
    setVerificationMethod(method);
    
    setNewCheckpoint({
      ...newCheckpoint,
      verificationMethod: method
    });
  };

  const handleAddCheckpoint = () => {
    // Validar datos básicos
    if (!newCheckpoint.name) {
      alert('El nombre del punto de control es obligatorio');
      return;
    }

    // Validar según el método de verificación
    if (verificationMethod === 'gps' || verificationMethod === 'both') {
      // Para GPS necesitamos coordenadas
      if (!newCheckpoint.coordinates.latitude || !newCheckpoint.coordinates.longitude) {
        alert('Las coordenadas GPS son obligatorias para verificación por GPS');
        return;
      }
    }

    if (verificationMethod === 'nfc') {
      // Para NFC solo necesitamos la etiqueta
      if (!newCheckpoint.nfcTag) {
        alert('Debes seleccionar una etiqueta NFC para verificación por NFC');
        return;
      }
    }

    // Crear una copia y añadir el nuevo punto
    const updatedCheckpoints = [...checkpoints];
    const checkpointToSave = {
      ...newCheckpoint,
      id: newCheckpoint.id || `cp-${Date.now()}`
    };

    if (editingIndex !== null) {
      // Editar existente
      updatedCheckpoints[editingIndex] = checkpointToSave;
    } else {
      // Añadir nuevo
      updatedCheckpoints.push(checkpointToSave);
    }

    onChange(updatedCheckpoints);
    resetForm();
  };

  const handleEdit = (checkpoint, index) => {
    // Determinar el método de verificación
    let method = 'gps';
    if (checkpoint.verificationMethod) {
      method = checkpoint.verificationMethod;
    } else if (checkpoint.nfcTag && (!checkpoint.coordinates || !checkpoint.coordinates.latitude)) {
      method = 'nfc';
    } else if (checkpoint.nfcTag) {
      method = 'both';
    }
    
    setVerificationMethod(method);
    
    setNewCheckpoint({
      ...checkpoint,
      verificationMethod: method,
      nfcTag: checkpoint.nfcTag || null,
      coordinates: {
        latitude: checkpoint.coordinates?.latitude || '',
        longitude: checkpoint.coordinates?.longitude || '',
        radius: checkpoint.coordinates?.radius || 10
      }
    });
    setEditingIndex(index);
    setIsAddingNew(true);
  };

  const handleDelete = (index) => {
    if (window.confirm('¿Estás seguro de eliminar este punto de control?')) {
      const updatedCheckpoints = [...checkpoints];
      updatedCheckpoints.splice(index, 1);
      onChange(updatedCheckpoints);
    }
  };

  const handleGetCurrentLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setNewCheckpoint({
            ...newCheckpoint,
            coordinates: {
              ...newCheckpoint.coordinates,
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            }
          });
          
          // Si estamos en modo NFC, cambiamos a ambos
          if (verificationMethod === 'nfc') {
            setVerificationMethod('both');
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('No se pudo obtener la ubicación actual. Por favor, introduce las coordenadas manualmente o usa el mapa.');
        }
      );
    } else {
      alert('Tu navegador no soporta geolocalización. Por favor, introduce las coordenadas manualmente o usa el mapa.');
    }
  };

  const handleOpenMapPicker = () => {
    setShowMapPicker(true);
  };

  const handleCloseMapPicker = () => {
    setShowMapPicker(false);
  };

  const handleSelectLocation = (location) => {
    setNewCheckpoint({
      ...newCheckpoint,
      coordinates: {
        ...newCheckpoint.coordinates,
        latitude: location.latitude,
        longitude: location.longitude
      }
    });
    
    // Si estamos en modo NFC, cambiamos a ambos
    if (verificationMethod === 'nfc') {
      setVerificationMethod('both');
    }
    
    setShowMapPicker(false);
  };

  // Funciones para NFC
  const handleOpenNFCReader = () => {
    setShowNFCReader(true);
  };

  const handleCloseNFCReader = () => {
    setShowNFCReader(false);
  };

  const handleNFCTagRead = (tagData) => {
    setNewCheckpoint({
      ...newCheckpoint,
      nfcTag: {
        id: tagData.id,
        data: tagData.data
      }
    });
    
    // Si estamos en modo GPS, cambiamos a ambos
    if (verificationMethod === 'gps') {
      setVerificationMethod('both');
    }
    
    setShowNFCReader(false);
  };

  const getVerificationBadge = (method) => {
    switch (method) {
      case 'gps':
        return <span className="verification-type gps">GPS</span>;
      case 'nfc':
        return <span className="verification-type nfc">NFC</span>;
      case 'both':
        return <span className="verification-type both">GPS + NFC</span>;
      default:
        return <span className="verification-type gps">GPS</span>;
    }
  };

  return (
    <div className="checkpoint-manager">
      <div className="header">
        <h3 className="title">Puntos de Control</h3>
        {!isAddingNew && (
          <button
            onClick={() => setIsAddingNew(true)}
            className="btn btn-add"
          >
            <Plus size={16} className="icon" />
            Añadir Punto
          </button>
        )}
      </div>

      {isAddingNew && (
        <div className="form-container">
          <div className="form-header">
            <h4 className="form-title">
              {editingIndex !== null ? 'Editar Punto' : 'Nuevo Punto de Control'}
            </h4>
            <button
              onClick={resetForm}
              className="btn-close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Selector de método de verificación */}
          <div className="verification-method-selector">
            <h5 className="selector-title">Método de Verificación</h5>
            <div className="method-options">
              <button 
                className={`method-option ${verificationMethod === 'gps' ? 'active' : ''}`}
                onClick={() => handleVerificationMethodChange('gps')}
              >
                <MapPin size={18} className="method-icon" />
                <span>GPS</span>
              </button>
              
              <button 
                className={`method-option ${verificationMethod === 'nfc' ? 'active' : ''}`}
                onClick={() => handleVerificationMethodChange('nfc')}
                disabled={!nfcSupported && nfcTags.length === 0}
              >
                <Smartphone size={18} className="method-icon" />
                <span>NFC</span>
                {!nfcSupported && nfcTags.length === 0 && (
                  <span className="disabled-note">No hay etiquetas disponibles</span>
                )}
              </button>
              
              <button 
                className={`method-option ${verificationMethod === 'both' ? 'active' : ''}`}
                onClick={() => handleVerificationMethodChange('both')}
                disabled={!nfcSupported && nfcTags.length === 0}
              >
                <div className="dual-icons">
                  <MapPin size={16} className="method-icon" />
                  <Smartphone size={16} className="method-icon" />
                </div>
                <span>Ambos</span>
                {!nfcSupported && nfcTags.length === 0 && (
                  <span className="disabled-note">No hay etiquetas disponibles</span>
                )}
              </button>
            </div>
            
            <div className="method-info">
              {verificationMethod === 'gps' && (
                <div className="info-message">
                  <Info size={16} className="info-icon" />
                  <span>La verificación por GPS permite confirmar el punto mediante la ubicación del dispositivo.</span>
                </div>
              )}
              
              {verificationMethod === 'nfc' && (
                <div className="info-message">
                  <Info size={16} className="info-icon" />
                  <span>La verificación por NFC requiere escanear una etiqueta física en el punto de control.</span>
                </div>
              )}
              
              {verificationMethod === 'both' && (
                <div className="info-message">
                  <Info size={16} className="info-icon" />
                  <span>El modo dual permite verificar el punto con NFC prioritariamente, usando GPS como respaldo.</span>
                </div>
              )}
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                Nombre del Punto *
              </label>
              <input
                type="text"
                value={newCheckpoint.name}
                onChange={(e) => setNewCheckpoint({ ...newCheckpoint, name: e.target.value })}
                className="form-input"
                placeholder="Ej: Entrada Principal"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Ubicación
              </label>
              <input
                type="text"
                value={newCheckpoint.location}
                onChange={(e) => setNewCheckpoint({ ...newCheckpoint, location: e.target.value })}
                className="form-input"
                placeholder="Ej: Edificio A, Planta 2"
              />
            </div>

            <div className="form-group form-group-full">
              <label className="form-label">
                Descripción
              </label>
              <textarea
                value={newCheckpoint.description}
                onChange={(e) => setNewCheckpoint({ ...newCheckpoint, description: e.target.value })}
                className="form-textarea"
                placeholder="Descripción del punto de control"
                rows="2"
              />
            </div>

            {/* Coordenadas GPS (Visibles solo si el método es GPS o Ambos) */}
            {(verificationMethod === 'gps' || verificationMethod === 'both') && (
              <>
                <div className="form-group form-group-full">
                  <label className="form-label">
                    Coordenadas GPS {verificationMethod === 'gps' ? '*' : '(Respaldo)'}
                  </label>
                  <div className="coordinates-container">
                    <div className="coordinates-inputs">
                      <input
                        type="number"
                        step="0.000001"
                        value={newCheckpoint.coordinates.latitude}
                        onChange={(e) => setNewCheckpoint({
                          ...newCheckpoint,
                          coordinates: {
                            ...newCheckpoint.coordinates,
                            latitude: e.target.value
                          }
                        })}
                        className="form-input"
                        placeholder="Latitud (Ej: 40.416775)"
                        required={verificationMethod === 'gps'}
                      />
                      <input
                        type="number"
                        step="0.000001"
                        value={newCheckpoint.coordinates.longitude}
                        onChange={(e) => setNewCheckpoint({
                          ...newCheckpoint,
                          coordinates: {
                            ...newCheckpoint.coordinates,
                            longitude: e.target.value
                          }
                        })}
                        className="form-input"
                        placeholder="Longitud (Ej: -3.703790)"
                        required={verificationMethod === 'gps'}
                      />
                    </div>
                    <div className="coordinates-actions">
                      <button
                        type="button"
                        onClick={handleGetCurrentLocation}
                        className="btn btn-location"
                      >
                        <MapPin size={16} className="icon" />
                        Ubicación Actual
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenMapPicker}
                        className="btn btn-map"
                      >
                        <Map size={16} className="icon" />
                        Seleccionar en Mapa
                      </button>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Radio de verificación (metros)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={newCheckpoint.coordinates.radius}
                    onChange={(e) => setNewCheckpoint({
                      ...newCheckpoint,
                      coordinates: {
                        ...newCheckpoint.coordinates,
                        radius: parseInt(e.target.value) || 10
                      }
                    })}
                    className="form-input"
                  />
                  <p className="form-hint">
                    Distancia permitida para registrar el punto (5-50m recomendado)
                  </p>
                </div>
              </>
            )}

            {/* Etiqueta NFC (Visible solo si el método es NFC o Ambos) */}
            {(verificationMethod === 'nfc' || verificationMethod === 'both') && (
              <div className="form-group form-group-full">
                <label className="form-label">
                  Etiqueta NFC {verificationMethod === 'nfc' ? '*' : '(Prioritario)'}
                </label>
                <div className="nfc-tag-container">
                  {newCheckpoint.nfcTag ? (
                    <div className="nfc-tag-info">
                      <div className="tag-details">
                        <Smartphone size={16} className="tag-icon" />
                        <span className="nfc-tag-id">ID: {newCheckpoint.nfcTag.id}</span>
                        {newCheckpoint.nfcTag.name && (
                          <span className="nfc-tag-name">({newCheckpoint.nfcTag.name})</span>
                        )}
                      </div>
                      <div className="nfc-actions">
                        {nfcSupported && (
                          <button
                            type="button"
                            className="btn-scan-new"
                            onClick={handleOpenNFCReader}
                          >
                            Escanear Otra
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-select-existing"
                          onClick={handleOpenNfcSelector}
                        >
                          {nfcSupported ? 'Seleccionar Existente' : 'Cambiar Etiqueta'}
                        </button>
                        <button
                          type="button"
                          className="btn-remove-nfc"
                          onClick={() => {
                            setNewCheckpoint({ ...newCheckpoint, nfcTag: null });
                            // Si estamos en modo NFC puro, cambiamos a GPS
                            if (verificationMethod === 'nfc') {
                              setVerificationMethod('gps');
                            }
                          }}
                        >
                          Quitar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="nfc-actions-empty">
                      {nfcSupported && (
                        <button
                          type="button"
                          className="btn btn-nfc-scan"
                          onClick={handleOpenNFCReader}
                        >
                          <Smartphone size={16} className="icon" />
                          Escanear Nueva Etiqueta
                        </button>
                      )}
                      <button
                        type="button"
                        className={`btn ${nfcSupported ? 'btn-nfc-select' : 'btn-nfc-scan'}`}
                        onClick={handleOpenNfcSelector}
                        disabled={loadingTags}
                        style={!nfcSupported ? {width: '100%'} : {}}
                      >
                        <Search size={16} className="icon" />
                        Seleccionar Etiqueta Existente
                      </button>
                    </div>
                  )}
                </div>
                {verificationMethod === 'both' && (
                  <p className="form-hint">
                    En modo dual, el sistema intentará verificar primero con NFC, y usará GPS como respaldo.
                  </p>
                )}
                {!nfcSupported && (
                  <p className="form-hint">
                    Este dispositivo no soporta la lectura NFC. Solo puedes seleccionar etiquetas ya registradas.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={resetForm}
              className="btn btn-cancel"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleAddCheckpoint}
              className="btn btn-save"
            >
              {editingIndex !== null ? 'Guardar Cambios' : 'Añadir Punto'}
            </button>
          </div>
        </div>
      )}

      {checkpoints.length === 0 && !isAddingNew ? (
        <div className="empty-state">
          <MapPin size={32} className="empty-icon" />
          <p className="empty-text">No hay puntos de control definidos</p>
          <p className="empty-subtext">
            Añade puntos de control para definir la ruta de vigilancia
          </p>
        </div>
      ) : (
        <div className="checkpoints-list">
          {checkpoints.map((checkpoint, index) => {
            // Determinar el método de verificación
            let method = checkpoint.verificationMethod || 'gps';
            if (!method) {
              if (checkpoint.nfcTag && checkpoint.coordinates?.latitude) {
                method = 'both';
              } else if (checkpoint.nfcTag) {
                method = 'nfc';
              } else {
                method = 'gps';
              }
            }
            
            return (
              <div
                key={checkpoint.id || index}
                className="checkpoint-item"
              >
                <div className="checkpoint-content">
                  <div className="checkpoint-info">
                    <h4 className="checkpoint-name">
                      {checkpoint.name}
                      {getVerificationBadge(method)}
                    </h4>
                    <div className="checkpoint-details">
                      {checkpoint.location && (
                        <div className="checkpoint-location">
                          <MapPin size={14} className="location-icon" />
                          {checkpoint.location}
                        </div>
                      )}
                      {checkpoint.nfcTag && (
                        <div className="checkpoint-nfc">
                          <Smartphone size={14} className="nfc-icon" />
                          NFC: {checkpoint.nfcTag.name || 
                                (checkpoint.nfcTag.id.length > 10 
                                  ? checkpoint.nfcTag.id.substring(0, 10) + '...' 
                                  : checkpoint.nfcTag.id)
                          }
                        </div>
                      )}
                      {method !== 'nfc' && checkpoint.coordinates && (
                        <div className="checkpoint-coordinates">
                          GPS: {checkpoint.coordinates.latitude},
                          {checkpoint.coordinates.longitude},
                          Radio: {checkpoint.coordinates.radius || 10}m
                        </div>
                      )}
                    </div>
                    {checkpoint.description && (
                      <p className="checkpoint-description">{checkpoint.description}</p>
                    )}
                  </div>
                  <div className="checkpoint-actions">
                    <button
                      onClick={() => handleEdit(checkpoint, index)}
                      className="btn-edit"
                      title="Editar"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(index)}
                      className="btn-delete"
                      title="Eliminar"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showMapPicker && (
        <MapLocationPicker
          initialCoordinates={newCheckpoint.coordinates.latitude && newCheckpoint.coordinates.longitude ? {
            latitude: parseFloat(newCheckpoint.coordinates.latitude),
            longitude: parseFloat(newCheckpoint.coordinates.longitude)
          } : null}
          onSelectLocation={handleSelectLocation}
          onClose={handleCloseMapPicker}
        />
      )}

      {showNFCReader && (
        <NFCReader
          onTagRead={handleNFCTagRead}
          onCancel={handleCloseNFCReader}
        />
      )}

      {showNfcSelector && (
        <NfcTagSelector
          tags={nfcTags}
          onSelectTag={handleSelectNfcTag}
          onCancel={() => setShowNfcSelector(false)}
          onRefresh={fetchNfcTags}
        />
      )}
    </div>
  );
};

export default CheckpointManager;